import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Phone, Mail, Inbox } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CONTACT } from "@/lib/content";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("lb_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("lb_token", data.access_token);
      setToken(data.access_token);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("lb_token");
    setToken(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--ink)" }}>
        <div className="w-full max-w-[420px]">
          <Link to="/" className="font-serif text-[20px] tracking-[0.12em] block mb-8" style={{ color: "var(--champagne)" }}>LUXEBOND</Link>
          <div className="p-9" style={{ border: "1px solid var(--line)", background: "rgba(239,230,211,0.02)" }}>
            <div className="font-mono-lbl text-[11px] mb-2" style={{ color: "var(--brass-bright)" }}>Admin Access</div>
            <h1 className="font-serif text-3xl mb-6" style={{ color: "var(--champagne)" }}>Sign in</h1>
            <form onSubmit={login} data-testid="admin-login-form">
              <label className="block mb-4">
                <span className="block font-mono-lbl text-[10px] mb-2" style={{ color: "var(--stone)" }}>Email</span>
                <input data-testid="admin-email" className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@luxebond.com" />
              </label>
              <label className="block mb-6">
                <span className="block font-mono-lbl text-[10px] mb-2" style={{ color: "var(--stone)" }}>Password</span>
                <input data-testid="admin-password" type="password" className="field-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
              </label>
              <button type="submit" disabled={loading} data-testid="admin-login-btn" className="btn btn-primary w-full justify-center disabled:opacity-60">
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return <Dashboard onLogout={logout} />;
}

function Dashboard({ onLogout }) {
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, s] = await Promise.all([api.get("/quotes"), api.get("/quotes/stats")]);
      setQuotes(q.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Could not load submissions.");
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (q) => {
    const next = q.status === "new" ? "contacted" : "new";
    try {
      await api.patch(`/quotes/${q.id}`, { status: next });
      setQuotes((list) => list.map((x) => (x.id === q.id ? { ...x, status: next } : x)));
      setStats((s) => ({ ...s, new: s.new + (next === "new" ? 1 : -1) }));
    } catch {
      toast.error("Could not update status.");
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--ink)" }}>
      <header className="sticky top-0 z-10" style={{ background: "rgba(12,11,9,0.9)", backdropFilter: "blur(14px)", borderBottom: "1px solid var(--line)" }}>
        <div className="wrap flex items-center justify-between py-5">
          <Link to="/" className="font-serif text-[19px] tracking-[0.12em]" style={{ color: "var(--champagne)" }}>LUXEBOND</Link>
          <div className="flex items-center gap-3">
            <button onClick={load} className="btn btn-ghost !py-2 !px-4 !text-[11px]" data-testid="admin-refresh"><RefreshCw size={14} /> Refresh</button>
            <button onClick={onLogout} className="btn btn-ghost !py-2 !px-4 !text-[11px]" data-testid="admin-logout"><LogOut size={14} /> Sign out</button>
          </div>
        </div>
      </header>

      <div className="wrap py-12">
        <div className="flex items-center gap-3 mb-2">
          <span className="inline-block w-9 h-px" style={{ background: "var(--brass-bright)" }} />
          <span className="font-mono-lbl text-[11px]" style={{ color: "var(--brass-bright)" }}>Quote Inbox</span>
        </div>
        <h1 className="font-serif text-4xl mb-8" style={{ color: "var(--champagne)" }}>Submissions</h1>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-px mb-10" style={{ background: "var(--line)", border: "1px solid var(--line)" }}>
          <Stat label="Total leads" value={stats.total} />
          <Stat label="New / unactioned" value={stats.new} accent />
          <Stat label="Contacted" value={Math.max(stats.total - stats.new, 0)} />
        </div>

        {loading ? (
          <p className="text-[14px]" style={{ color: "var(--stone)" }}>Loading…</p>
        ) : quotes.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-24" style={{ color: "var(--stone-dim)" }} data-testid="admin-empty">
            <Inbox size={40} />
            <p className="text-[14px]">No submissions yet. New quote requests will appear here.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-px" style={{ background: "var(--line)", border: "1px solid var(--line)" }} data-testid="admin-quote-list">
            {quotes.map((q) => (
              <div key={q.id} className="grid md:grid-cols-[1.4fr_1fr_auto] gap-5 p-6 items-start" style={{ background: "var(--ink)" }} data-testid="admin-quote-row">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-serif text-xl" style={{ color: "var(--champagne)" }}>{q.name}</h3>
                    <span className="font-mono-lbl text-[9.5px] px-2 py-1" style={{ border: "1px solid var(--line)", color: q.status === "new" ? "var(--brass-bright)" : "var(--stone-dim)" }}>{q.status}</span>
                  </div>
                  <div className="font-mono-lbl text-[10px] mt-2" style={{ color: "var(--stone-dim)" }}>{q.project_type}</div>
                  {q.details && <p className="text-[13.5px] leading-relaxed mt-3 max-w-[520px]" style={{ color: "var(--stone)" }}>{q.details}</p>}
                </div>
                <div className="flex flex-col gap-2 text-[13px]">
                  <a href={`tel:${q.phone}`} className="flex items-center gap-2 hover:text-[var(--brass-bright)] transition-colors" style={{ color: "var(--champagne)" }}><Phone size={13} style={{ color: "var(--brass)" }} /> {q.phone}</a>
                  {q.email && <a href={`mailto:${q.email}`} className="flex items-center gap-2 hover:text-[var(--brass-bright)] transition-colors break-all" style={{ color: "var(--stone)" }}><Mail size={13} style={{ color: "var(--brass)" }} /> {q.email}</a>}
                  <span className="text-[11px]" style={{ color: "var(--stone-dim)" }}>{new Date(q.created_at).toLocaleString()}</span>
                </div>
                <button onClick={() => toggleStatus(q)} className="btn btn-ghost !py-2 !px-4 !text-[10.5px] whitespace-nowrap" data-testid="admin-toggle-status">
                  Mark {q.status === "new" ? "contacted" : "new"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="p-7" style={{ background: "var(--ink)" }}>
      <div className="font-serif text-[40px] leading-none" style={{ color: accent ? "var(--brass-bright)" : "var(--champagne)" }}>{value}</div>
      <div className="text-[12px] mt-2" style={{ color: "var(--stone)" }}>{label}</div>
    </div>
  );
}
